import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { currency, StatusBadge, Stars } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { Archive, Calendar, MapPin, DollarSign, Trophy, X, Loader2 } from "lucide-react";

export default function History() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shifts", { params: { scope: "history" } });
        setShifts(data.shifts || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const completed = shifts.filter(s => s.status === "completed");
  const cancelled = shifts.filter(s => s.status === "cancelled");
  const totalValue = completed.reduce((acc, s) => acc + Number(s.final_rate || 0), 0);
  const label = user?.role === "comercio" ? "Total pago" : "Total recebido";

  return (
    <div className="space-y-6 anim-fade-up">
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-2">
          <Archive className="w-4 h-4" /> Histórico interno
        </div>
        <h1 className="text-3xl font-display font-extrabold">Diárias concluídas</h1>
        <p className="text-zinc-400 text-sm mt-1">
          Suas diárias finalizadas e canceladas ficam guardadas aqui — fora do feed principal.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="cw-card p-4">
              <Trophy className="w-4 h-4 text-amber-400 mb-2" />
              <div className="text-2xl font-display font-bold">{completed.length}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">Concluídas</div>
            </div>
            <div className="cw-card p-4">
              <DollarSign className="w-4 h-4 text-emerald-400 mb-2" />
              <div className="text-2xl font-display font-bold text-emerald-400">{currency(totalValue)}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{label}</div>
            </div>
            <div className="cw-card p-4">
              <X className="w-4 h-4 text-red-400 mb-2" />
              <div className="text-2xl font-display font-bold text-red-400">{cancelled.length}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">Canceladas</div>
            </div>
          </div>

          {shifts.length === 0 ? (
            <div className="cw-card p-10 text-center text-zinc-400" data-testid="history-empty">
              Nenhuma diária concluída ainda. Quando você finalizar uma, ela aparecerá aqui.
            </div>
          ) : (
            <div className="space-y-3" data-testid="history-list">
              {shifts.map(s => (
                <Link key={s.shift_id} to={`/shifts/${s.shift_id}`} data-testid={`history-item-${s.shift_id}`}
                      className="cw-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-zinc-900/60 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{s.title}</h3>
                      <StatusBadge status={s.status} />
                    </div>
                    <div className="text-xs text-zinc-400 flex flex-wrap gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.date}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.city || "—"}</span>
                      <span className="text-zinc-500">
                        {user?.role === "comercio" ? `com ${s.worker_name || "—"}` : `${s.commerce_name}`}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-lg font-display font-bold ${s.status === "completed" ? "text-emerald-400" : "text-zinc-500 line-through"}`}>
                      {currency(s.final_rate)}
                    </div>
                    {s.status === "completed" && user?.role === "comercio" && s.commerce_rating && (
                      <div className="flex justify-end mt-1"><Stars value={s.commerce_rating} /></div>
                    )}
                    {s.status === "completed" && user?.role === "acougueiro" && s.worker_rating && (
                      <div className="flex justify-end mt-1"><Stars value={s.worker_rating} /></div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
